import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  Row,
  Col,
  Typography,
  Image,
  Descriptions,
  Tag,
  Table,
  Button,
  Space,
  Spin,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Divider,
  Alert,
  Statistic,
  Tabs,
} from 'antd';
import {
  ArrowLeftOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  InboxOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  PlayCircleOutlined,
  LinkOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { getProductFullInfo, createListing, getProductDetail } from '../../api';
import { useSupplier } from '../../context/SupplierContext';
import { formatPrice, getBestPrice, calculateSuggestedPrice, calculateProfitMargin } from '../../utils/price';
import type { GigaProductDetail, GigaPrice, GigaInventory } from '../../types';

const { Title, Text } = Typography;

// 关联产品信息类型
interface AssociatedProductInfo {
  sku: string;
  productName?: string;
  mainImageUrl?: string;
  loading: boolean;
}

const ProductDetail: React.FC = () => {
  const { sku } = useParams<{ sku: string }>();
  const navigate = useNavigate();
  const { supplier } = useSupplier();
  const [searchParams] = useSearchParams();
  
  const cachedInventory = searchParams.get('qty') ? parseInt(searchParams.get('qty')!, 10) : null;
  
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<GigaProductDetail | null>(null);
  const [price, setPrice] = useState<GigaPrice | null>(null);
  const [inventory, setInventory] = useState<GigaInventory | null>(null);
  
  // 当前选中的大图
  const [selectedImage, setSelectedImage] = useState<string>('');
  
  // 关联产品详情
  const [associatedProducts, setAssociatedProducts] = useState<AssociatedProductInfo[]>([]);
  
  // 上架弹窗
  const [listingVisible, setListingVisible] = useState(false);
  const [listingLoading, setListingLoading] = useState(false);
  const [form] = Form.useForm();
  
  // 用于取消请求的 AbortController
  const abortControllerRef = useRef<AbortController | null>(null);

  // 加载产品信息
  const loadProductInfo = async (productSku: string) => {
    // 取消之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    setLoading(true);
    setAssociatedProducts([]);
    
    try {
      const { detail, price, inventory } = await getProductFullInfo(productSku, supplier);
      
      setDetail(detail || null);
      setPrice(price || null);
      setInventory(inventory || null);
      
      // 设置默认选中的图片
      if (detail?.mainImageUrl) {
        setSelectedImage(detail.mainImageUrl);
      }
      
      // 设置表单默认值
      if (detail && price) {
        const costPrice = getBestPrice(price);
        form.setFieldsValue({
          custom_title: detail.productName,
          selling_price: calculateSuggestedPrice(costPrice, price.shippingFee || 0),
        });
      }
      
      // 加载关联产品详情
      const extDetail = detail as GigaProductDetail & { associateProductList?: string[] };
      if (extDetail?.associateProductList && extDetail.associateProductList.length > 0) {
        loadAssociatedProducts(extDetail.associateProductList);
      }
    } catch (error) {
      // 忽略取消的请求
      if (axios.isCancel(error)) {
        return;
      }
      message.error('加载产品信息失败');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (sku) {
      loadProductInfo(sku);
    }
    
    // 如果 URL 带了 action=list 参数，自动打开上架弹窗
    if (searchParams.get('action') === 'list') {
      setListingVisible(true);
    }
    
    // 组件卸载时取消请求
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [sku, searchParams, supplier]);
  
  // 加载关联产品详情
  const loadAssociatedProducts = async (skuList: string[]) => {
    // 先设置 loading 状态
    setAssociatedProducts(skuList.map(sku => ({ sku, loading: true })));
    
    try {
      const res = await getProductDetail(skuList, undefined, supplier);
      if (res.success && res.data) {
        setAssociatedProducts(skuList.map(sku => {
          const productDetail = res.data?.find(p => p.sku === sku);
          return {
            sku,
            productName: productDetail?.productName,
            mainImageUrl: productDetail?.mainImageUrl,
            loading: false,
          };
        }));
      } else {
        // 如果获取失败，保留 SKU 但标记为加载完成
        setAssociatedProducts(skuList.map(sku => ({ sku, loading: false })));
      }
    } catch (error) {
      // 忽略取消的请求
      if (axios.isCancel(error)) {
        return;
      }
      // 加载失败时保留 SKU
      setAssociatedProducts(skuList.map(sku => ({ sku, loading: false })));
    }
  };

  // 处理上架
  const handleListing = async (values: Record<string, unknown>) => {
    if (!detail || !price) {
      message.error('产品信息不完整');
      return;
    }

    setListingLoading(true);
    try {
      const res = await createListing({
        giga_sku: sku!,
        giga_product_name: detail.productName,
        giga_main_image: detail.mainImageUrl,
        giga_category: detail.category,
        giga_price: getBestPrice(price),
        giga_shipping_fee: price.shippingFee,
        giga_map_price: price.mapPrice,
        selling_price: values.selling_price as number,
        custom_title: values.custom_title as string,
        custom_description: values.custom_description as string,
        shopify_product_type: detail.category,
        giga_available_qty: availableInventory,
        // 新增：传递 GIGA 描述和所有图片 URLs
        giga_description: detail.description || '',
        giga_image_urls: JSON.stringify(detail.imageUrls || []),
      }, supplier);

      if (res.success) {
        const data = res.data as Record<string, unknown> | undefined;
        const odooSync = data?.odooSync as Record<string, unknown> | undefined;

        if (odooSync?.success) {
          message.success('上架成功！Odoo 产品已创建，Shopify 将自动同步');
        } else if (odooSync && !odooSync.success) {
          message.warning('上架记录已创建，但 Odoo 产品同步失败，请在上架管理中查看');
        } else {
          message.success('上架记录创建成功！');
        }

        setListingVisible(false);
        navigate('/listings');
      } else {
        message.error(res.error || '创建失败');
      }
    } catch {
      message.error('上架失败，请重试');
    } finally {
      setListingLoading(false);
    }
  };

  // 计算利润
  const sellingPrice = Form.useWatch('selling_price', form);
  const costPrice = price ? getBestPrice(price) : 0;
  const shippingFee = price?.shippingFee || 0;
  const totalCost = costPrice + shippingFee;
  const profitMargin = sellingPrice ? calculateProfitMargin(sellingPrice, costPrice, shippingFee) : 0;

  // 所有图片列表（主图 + 其他图片）
  const allImages = useMemo(() => {
    if (!detail) return [];
    const images: string[] = [];
    if (detail.mainImageUrl) {
      images.push(detail.mainImageUrl);
    }
    if (detail.imageUrls) {
      detail.imageUrls.forEach(url => {
        if (!images.includes(url)) {
          images.push(url);
        }
      });
    }
    return images;
  }, [detail]);

  // 获取组装后的尺寸信息
  const assembledDimensions = useMemo(() => {
    if (!detail) return null;
    const d = detail as GigaProductDetail & {
      assembledLength?: string;
      assembledWidth?: string;
      assembledHeight?: string;
      assembledWeight?: string;
    };
    if (d.assembledLength || d.assembledWidth || d.assembledHeight) {
      return {
        length: d.assembledLength,
        width: d.assembledWidth,
        height: d.assembledHeight,
        weight: d.assembledWeight,
      };
    }
    return null;
  }, [detail]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Title level={4}>产品不存在</Title>
        <Button type="primary" onClick={() => navigate('/products')}>
          返回产品列表
        </Button>
      </div>
    );
  }

  // 优先使用 API 返回的库存，如果为 0 或 undefined 则使用 URL 参数传递的缓存值
  const apiInventory = inventory?.sellerInventoryInfo?.sellerAvailableInventory;
  const availableInventory = (apiInventory && apiInventory > 0) ? apiInventory : (cachedInventory || 0);

  // 扩展类型以包含额外字段
  const extendedDetail = detail as GigaProductDetail & {
    attributes?: Record<string, string>;
    comboInfo?: Array<{
      sku: string;
      qty: number;
      weight?: number;
      length?: number;
      width?: number;
      height?: number;
      weightKg?: number;
      lengthCm?: number;
      widthCm?: number;
      heightCm?: number;
    }>;
    fileUrls?: string[];
    videoUrls?: string[];
    firstArrivalDate?: string;
    customized?: string;
    whiteLabel?: string;
    newArrivalFlag?: boolean;
    skuAvailable?: boolean;
  };

  return (
    <div>
      {/* 返回按钮 */}
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/products')}
        style={{ marginBottom: 16 }}
      >
        返回产品列表
      </Button>

      <Row gutter={24}>
        {/* 左侧：产品图片 */}
        <Col xs={24} md={10}>
          <Card bodyStyle={{ padding: 16 }}>
            {/* 大图展示 */}
            <div style={{ 
              width: '100%', 
              aspectRatio: '1', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: 8,
              overflow: 'hidden',
              marginBottom: 16,
            }}>
              <Image
                src={selectedImage || detail.mainImageUrl || 'https://via.placeholder.com/400x400?text=No+Image'}
                alt={detail.productName}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                fallback="https://via.placeholder.com/400x400?text=Error"
                preview={{
                  src: selectedImage || detail.mainImageUrl,
                }}
              />
            </div>
            
            {/* 缩略图列表 - 点击切换大图 */}
            {allImages.length > 1 && (
              <div style={{ 
                display: 'flex', 
                gap: 8, 
                flexWrap: 'wrap',
                padding: '8px 0',
                borderTop: '1px solid rgba(79, 110, 247, 0.15)',
              }}>
                {allImages.map((url, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedImage(url)}
                    style={{
                      width: 60,
                      height: 60,
                      border: selectedImage === url ? '2px solid #4f6ef7' : '1px solid rgba(79, 110, 247, 0.2)',
                      borderRadius: 4,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'border-color 0.3s',
                    }}
                  >
                    <img
                      src={url}
                      alt={`${detail.productName} - ${index + 1}`}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>

        {/* 右侧：产品信息 */}
        <Col xs={24} md={14}>
          <Card>
            {/* 产品标题和标签 */}
            <div style={{ marginBottom: 16 }}>
              <Title level={3} style={{ marginBottom: 8 }}>{detail.productName}</Title>
              <Space wrap>
                <Text type="secondary">Item Code: {detail.sku}</Text>
                {extendedDetail.firstArrivalDate && (
                  <Text type="secondary">首次到库时间: {extendedDetail.firstArrivalDate}</Text>
                )}
                {detail.sellerInfo?.sellerReturnRate && (
                  <Text type="secondary">退返品率: {detail.sellerInfo.sellerReturnRate}</Text>
                )}
              </Space>
            </div>
            
            {/* 价格信息 */}
            {price && (
              <div style={{ 
                background: 'rgba(0, 0, 0, 0.2)', 
                padding: 16, 
                borderRadius: 8,
                marginBottom: 16,
              }}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title={<Text style={{ color: '#a0b1c5' }}>分销价</Text>}
                      value={getBestPrice(price)}
                      precision={2}
                      prefix={<DollarOutlined />}
                      valueStyle={{ color: '#00d4ff', fontSize: 28, fontWeight: 700, textShadow: '0 0 15px rgba(0, 212, 255, 0.3)' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title={<Text style={{ color: '#a0b1c5' }}>运费</Text>}
                      value={price.shippingFee || 0}
                      precision={2}
                      prefix={<DollarOutlined />}
                      valueStyle={{ color: '#fff' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title={<Text style={{ color: '#a0b1c5' }}>可用库存</Text>}
                      value={availableInventory}
                      suffix="件"
                      prefix={<InboxOutlined />}
                      valueStyle={{
                        color: availableInventory > 10 ? '#52c41a' : availableInventory > 0 ? '#faad14' : '#ff4d4f',
                        fontWeight: 600
                      }}
                    />
                  </Col>
                </Row>
                
                {price.mapPrice && price.mapPrice > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary">
                      建议零售价: {formatPrice(price.mapPrice)}
                    </Text>
                  </div>
                )}
              </div>
            )}

            {/* 操作按钮 */}
            <Space style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                size="large"
                icon={<ShoppingCartOutlined />}
                onClick={() => setListingVisible(true)}
                disabled={!price || availableInventory === 0}
              >
                上架到 Shopify
              </Button>
            </Space>

            {availableInventory === 0 && (
              <Alert
                message="该产品暂时缺货，无法上架"
                type="warning"
                style={{ marginBottom: 16 }}
                showIcon
              />
            )}

            {/* 关联产品 - 放在操作按钮下方，参考官网布局 */}
            {associatedProducts.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Title level={5}>
                  <LinkOutlined style={{ marginRight: 8 }} />
                  关联产品
                </Title>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 8,
                  maxHeight: 200,
                  overflowY: 'auto',
                }}>
                  {associatedProducts.map((product) => (
                    <div
                      key={product.sku}
                      onClick={() => navigate(`/products/${product.sku}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 12px',
                      border: product.sku === sku ? '2px solid #4f6ef7' : '1px solid rgba(79, 110, 247, 0.15)',
                      borderRadius: 4,
                      cursor: 'pointer',
                      background: product.sku === sku ? 'rgba(79, 110, 247, 0.15)' : 'rgba(15, 20, 53, 0.5)',
                        transition: 'all 0.3s',
                        minWidth: 180,
                        maxWidth: 280,
                      }}
                    >
                      {product.loading ? (
                        <Spin size="small" />
                      ) : (
                        <>
                          <img
                            src={product.mainImageUrl || 'https://via.placeholder.com/40x40?text=N/A'}
                            alt={product.sku}
                            style={{
                              width: 40,
                              height: 40,
                              objectFit: 'cover',
                              borderRadius: 4,
                              marginRight: 8,
                              flexShrink: 0,
                            }}
                          />
                          <div style={{ overflow: 'hidden', flex: 1 }}>
                            <Text 
                              ellipsis 
                              style={{ 
                                display: 'block', 
                                fontSize: 12,
                                fontWeight: product.sku === sku ? 600 : 400,
                                color: product.sku === sku ? '#00d4ff' : undefined,
                              }}
                            >
                              {product.sku === sku && <CheckCircleOutlined style={{ marginRight: 4 }} />}
                              {product.productName || product.sku}
                            </Text>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Divider />

            {/* 卖家信息 */}
            {detail.sellerInfo && (
              <div>
                <Title level={5}>卖家信息</Title>
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="店铺名称">
                    {detail.sellerInfo.sellerStore || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="店铺代码">
                    {detail.sellerInfo.sellerCode || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="店铺评分">
                    <Tag color="blue">{detail.sellerInfo.gigaIndex || '-'}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="退返品率">
                    {detail.sellerInfo.sellerReturnRate || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="退货批准率">
                    {detail.sellerInfo.sellerReturnApprovalRate || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="消息回复率">
                    {detail.sellerInfo.sellerMessageResponseRate || '-'}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* 产品特点 */}
      {detail.characteristics && detail.characteristics.length > 0 && (
        <Card title="产品特点" style={{ marginTop: 24 }}>
          {!detail.description && (
            <Alert
              message="GIGA 图文描述缺失"
              description="当前产品的 description 字段为空，系统会使用这些产品特点生成 Shopify 描述。"
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {detail.characteristics.map((char, index) => (
              <li key={index} style={{ marginBottom: 8, lineHeight: 1.6 }}>
                {char}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* 产品详情 - 使用 Tabs 组织 */}
      <Card style={{ marginTop: 24 }}>
        <Tabs
          defaultActiveKey="basic"
          items={[
            {
              key: 'basic',
              label: '产品详情',
              children: (
                <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="small">
                  <Descriptions.Item label="分类">{detail.category || '-'}</Descriptions.Item>
                  <Descriptions.Item label="UPC">{detail.upc || '-'}</Descriptions.Item>
                  <Descriptions.Item label="MPN">{detail.mpn || '-'}</Descriptions.Item>
                  <Descriptions.Item label="尺寸 (长×宽×高)">
                    {assembledDimensions 
                      ? `${assembledDimensions.length || '-'}" × ${assembledDimensions.width || '-'}" × ${assembledDimensions.height || '-'}"`
                      : detail.lengthCm && detail.widthCm && detail.heightCm
                        ? `${detail.lengthCm} × ${detail.widthCm} × ${detail.heightCm} cm`
                        : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="重量">
                    {assembledDimensions?.weight 
                      ? `${assembledDimensions.weight} lb`
                      : detail.weightKg 
                        ? `${detail.weightKg} kg` 
                        : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="产地">{detail.placeOfOrigin || '-'}</Descriptions.Item>
                  <Descriptions.Item label="是否Combo">
                    {detail.comboFlag ? <Tag color="blue">是</Tag> : <Tag>否</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="是否超大件">
                    {detail.overSizeFlag ? <Tag color="orange">是</Tag> : <Tag>否</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="含锂电池">
                    {detail.lithiumBatteryContained === 'Yes' ? <Tag color="red">是</Tag> : <Tag>否</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="是否定制">
                    {extendedDetail.customized === 'Yes' ? <Tag color="purple">是</Tag> : <Tag>否</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="是否白标">
                    {extendedDetail.whiteLabel === 'Yes' ? <Tag color="cyan">是</Tag> : <Tag>否</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="是否新品">
                    {extendedDetail.newArrivalFlag ? <Tag color="green">新品</Tag> : <Tag>否</Tag>}
                  </Descriptions.Item>
                </Descriptions>
              ),
            },
            // 产品属性
            extendedDetail.attributes && Object.keys(extendedDetail.attributes).length > 0 ? {
              key: 'attributes',
              label: '产品属性',
              children: (
                <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
                  {Object.entries(extendedDetail.attributes).map(([key, value]) => (
                    <Descriptions.Item key={key} label={key}>
                      {value || '-'}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              ),
            } : null,
            // 图文描述
            detail.description ? {
              key: 'description',
              label: '图文描述',
              children: (
                <div 
                  className="product-description"
                  dangerouslySetInnerHTML={{ __html: detail.description }}
                  style={{
                    lineHeight: 1.8,
                    // 确保图片响应式
                  }}
                />
              ),
            } : null,
            // Combo 信息
            extendedDetail.comboInfo && extendedDetail.comboInfo.length > 0 ? {
              key: 'combo',
              label: `包装信息 (${extendedDetail.comboInfo.length}件)`,
              children: (
                <Table
                  dataSource={extendedDetail.comboInfo}
                  rowKey="sku"
                  pagination={false}
                  size="small"
                  columns={[
                    { title: 'SKU', dataIndex: 'sku', key: 'sku' },
                    { title: '数量', dataIndex: 'qty', key: 'qty' },
                    { 
                      title: '重量', 
                      key: 'weight',
                      render: (_, record) => record.weight ? `${record.weight} lb` : '-',
                    },
                    { 
                      title: '尺寸 (L×W×H)', 
                      key: 'dimensions',
                      render: (_, record) => 
                        record.length && record.width && record.height
                          ? `${record.length}" × ${record.width}" × ${record.height}"`
                          : '-',
                    },
                  ]}
                />
              ),
            } : null,
            // 文件资料
            (extendedDetail.fileUrls && extendedDetail.fileUrls.length > 0) || 
            (extendedDetail.videoUrls && extendedDetail.videoUrls.length > 0) ? {
              key: 'files',
              label: '文件资料',
              children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {extendedDetail.fileUrls && extendedDetail.fileUrls.length > 0 && (
                    <div>
                      <Title level={5}><FilePdfOutlined /> 文档</Title>
                      <Space wrap>
                        {extendedDetail.fileUrls.map((url, index) => (
                          <Button 
                            key={index} 
                            icon={<FileTextOutlined />}
                            href={url}
                            target="_blank"
                          >
                            文档 {index + 1}
                          </Button>
                        ))}
                      </Space>
                    </div>
                  )}
                  {extendedDetail.videoUrls && extendedDetail.videoUrls.length > 0 && (
                    <div>
                      <Title level={5}><PlayCircleOutlined /> 视频</Title>
                      <Space wrap>
                        {extendedDetail.videoUrls.map((url, index) => (
                          <Button 
                            key={index} 
                            icon={<PlayCircleOutlined />}
                            href={url}
                            target="_blank"
                          >
                            视频 {index + 1}
                          </Button>
                        ))}
                      </Space>
                    </div>
                  )}
                </Space>
              ),
            } : null,
          ].filter(Boolean) as NonNullable<typeof Tabs.prototype.props.items>}
        />
      </Card>

      {/* 库存分布 */}
      {inventory?.sellerInventoryInfo?.sellerInventoryDistribution && 
       inventory.sellerInventoryInfo.sellerInventoryDistribution.length > 0 && (
        <Card title="库存分布" style={{ marginTop: 24 }}>
          <Table
            dataSource={inventory.sellerInventoryInfo.sellerInventoryDistribution}
            rowKey="warehouseCode"
            pagination={false}
            size="small"
            columns={[
              { title: '仓库', dataIndex: 'warehouseCode', key: 'warehouseCode' },
              {
                title: '可用库存',
                key: 'available',
                render: (_, record) => {
                  if (record.availableQtyMin === record.availableQtyMax) {
                    return record.availableQtyMin;
                  }
                  return `${record.availableQtyMin} - ${record.availableQtyMax}`;
                },
              },
            ]}
          />
        </Card>
      )}

      {/* 阶梯价 */}
      {price?.spotPrice && price.spotPrice.length > 0 && (
        <Card title="阶梯价格" style={{ marginTop: 24 }}>
          <Table
            dataSource={price.spotPrice}
            rowKey={(record) => `${record.minQuantity}-${record.maxQuantity}`}
            pagination={false}
            size="small"
            columns={[
              {
                title: '数量区间',
                key: 'quantity',
                render: (_, record) => `${record.minQuantity} - ${record.maxQuantity}`,
              },
              {
                title: '单价',
                dataIndex: 'price',
                key: 'price',
                render: (price) => formatPrice(price),
              },
              {
                title: '折扣价',
                dataIndex: 'discountedSpotPrice',
                key: 'discountedSpotPrice',
                render: (price) => (price ? formatPrice(price) : '-'),
              },
            ]}
          />
        </Card>
      )}

      {/* 上架弹窗 */}
      <Modal
        title="上架到 Shopify"
        open={listingVisible}
        onCancel={() => setListingVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleListing}
        >
          <Form.Item
            name="custom_title"
            label="产品标题"
            rules={[{ required: true, message: '请输入产品标题' }]}
          >
            <Input placeholder="自定义在 Shopify 显示的标题" />
          </Form.Item>

          <Form.Item
            name="selling_price"
            label="销售价格 (USD)"
            rules={[{ required: true, message: '请输入销售价格' }]}
            extra={
              <Space>
                <Text type="secondary">
                  成本: {formatPrice(totalCost)}{shippingFee > 0 ? `（含运费 ${formatPrice(shippingFee)}）` : ''}
                </Text>
                <Text type={profitMargin >= 20 ? 'success' : 'warning'}>
                  利润率: {profitMargin.toFixed(1)}%
                </Text>
              </Space>
            }
          >
            <InputNumber
              min={0}
              precision={2}
              style={{ width: '100%' }}
              prefix="$"
              placeholder="设置您的销售价格"
            />
          </Form.Item>

          <Form.Item name="custom_description" label="产品描述（可选）">
            <Input.TextArea rows={4} placeholder="自定义产品描述" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={listingLoading}>
                确认上架
              </Button>
              <Button onClick={() => setListingVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 产品描述样式 - 深色主题 */}
      <style>{`
        .product-description {
          max-width: 100%;
          overflow-x: auto;
          color: rgba(255, 255, 255, 0.85);
        }
        .product-description img {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 16px auto;
        }
        .product-description table {
          width: 100%;
          border-collapse: collapse;
          margin: 16px 0;
        }
        .product-description td {
          padding: 12px 16px;
          border: 1px solid rgba(79, 110, 247, 0.15);
          color: rgba(255, 255, 255, 0.85);
        }
        .product-description tr td:first-child {
          background: rgba(0, 0, 0, 0.2);
          font-weight: 500;
          width: 40%;
        }
        .product-description a {
          color: #00d4ff;
        }
        .product-description h1,
        .product-description h2,
        .product-description h3,
        .product-description h4,
        .product-description h5 {
          color: rgba(255, 255, 255, 0.92);
        }
      `}</style>
    </div>
  );
};

export default ProductDetail;
